import Button from "react-bootstrap/Button";
import styled, { css } from "styled-components/macro";
import { theme } from "ui/theme";
import { UIButtonProps } from "./Button";

const primaryColor = theme.ColorSpace500;
const secondaryColor = theme.ColorWhite;
const activeColor = theme.ColorBlurple500;
const disabledColor = theme.ColorSpace100;

const primaryOnDarkColor = theme.ColorWhite;
const secondaryOnDarkColor = theme.ColorSpace500;
const activeOnDarkColor = theme.ColorBlurple100;
const disabledOnDarkColor = theme.ColorSpace300;

// react bootstrap removes the default focus outline and adds a custom focus state
// using a box shadow. We remove that custom focus state and implement our own,
// which uses a border on a pseudo element.
const commonStyles = css`
  font-family: ${({ theme }) => theme.FontFamilyButton};
  // Required for the focus border to work.
  position: relative;

  &:focus {
    box-shadow: none !important;
    outline: 2px solid ${activeColor};
    outline-offset: 2px;
  }
  &:disabled,
  &.disabled,
  &[aria-disabled="true"] {
    // override the react bootstrap button 65% opacity styling when disabled
    opacity: 100%;
  }
`;

type StyledButtonProps = Omit<
  UIButtonProps,
  "analyticsName" | "requestInProgress"
>;

export const $Button = styled(Button)<StyledButtonProps>`
  &.btn-block {
    width: 100%;
  }

  &.btn-block + .btn-block {
    margin-top: 0.5rem;
  }
`;

export const $LinkButton = styled($Button).attrs({ variant: "link" })`
  ${commonStyles}

  // Override the react boostrap button style that removes the underline on links
  text-decoration: underline;
  color: ${primaryColor};

  svg path {
    // For icons inside the button. This can be overriden by passing a color prop
    // into the icon component.
    fill: ${primaryColor};
  }

  &:focus,
  &:hover,
  &:active {
    color: ${activeColor} !important;

    svg path {
      fill: ${activeColor};
    }
  }
  &:focus {
    outline-offset: -2px;
  }
  &:disabled,
  &.disabled,
  &[aria-disabled="true"] {
    color: ${disabledColor} !important;
  }
`;

export const $LinkButtonOnDark = styled($LinkButton)`
  color: ${primaryOnDarkColor};

  svg path {
    // For icons inside the button. This can be overriden by passing a color prop
    // into the icon component.
    fill: ${primaryOnDarkColor};
  }

  &:focus,
  &:hover,
  &:active {
    color: ${activeOnDarkColor} !important;

    svg path {
      fill: ${activeOnDarkColor};
    }
  }
  &:focus {
    outline-color: ${activeOnDarkColor} !important;
  }
  &:disabled,
  &.disabled,
  &[aria-disabled="true"] {
    color: ${disabledOnDarkColor} !important;
  }
`;

export const $PrimaryButton = styled($Button)`
  ${commonStyles}

  display: inline-flex;
  padding: 0 1.75rem;
  // Using min-height so the button will expand if we somehow get into a state where the text wraps.
  min-height: 3rem;
  align-items: center;
  justify-content: center;
  color: ${secondaryColor};
  background-color: ${primaryColor};
  border-color: ${primaryColor};
  border-radius: 2.25rem;
  border-width: 3px;

  &:focus,
  &:hover,
  &:not(:disabled):not(.disabled):active,
  .show > &.dropdown-toggle {
    color: ${secondaryColor};
    background-color: ${activeColor} !important;
    border-color: ${activeColor} !important;
  }
  &:disabled,
  &.disabled,
  &[aria-disabled="true"] {
    background-color: ${disabledColor};
    border-color: ${disabledColor};
  }
`;

export const $PrimaryButtonOnDark = styled($PrimaryButton)`
  color: ${secondaryOnDarkColor};
  background-color: ${primaryOnDarkColor};
  border-color: ${primaryOnDarkColor};
  &:focus,
  &:hover,
  &:not(:disabled):not(.disabled):active,
  .show > &.dropdown-toggle {
    color: ${secondaryOnDarkColor} !important;
    background-color: ${activeOnDarkColor} !important;
    border-color: ${activeOnDarkColor} !important;
  }
  &:focus {
    outline-color: ${activeOnDarkColor} !important;
  }
  &:disabled,
  &.disabled,
  &[aria-disabled="true"] {
    color: ${secondaryOnDarkColor};
    background-color: ${disabledOnDarkColor};
    border-color: ${disabledOnDarkColor};
  }
`;

export const $SecondaryButton = styled($PrimaryButton)`
  color: ${primaryColor};
  background-color: transparent;
  border-color: ${primaryColor};
  &:focus,
  &:hover,
  &:not(:disabled):not(.disabled):active,
  .show > &.dropdown-toggle {
    color: ${activeColor} !important;
    background-color: transparent !important;
    border-color: ${activeColor} !important;
  }

  &:disabled,
  &.disabled,
  &[aria-disabled="true"] {
    color: ${disabledColor};
    background-color: transparent;
    border-color: ${disabledColor};
  }
`;

export const $SecondaryButtonOnDark = styled($SecondaryButton)`
  color: ${primaryOnDarkColor};
  border-color: ${primaryOnDarkColor};
  &:focus,
  &:hover,
  &:not(:disabled):not(.disabled):active,
  .show > &.dropdown-toggle {
    color: ${activeOnDarkColor} !important;
    border-color: ${activeOnDarkColor} !important;
  }
  &:focus {
    outline-color: ${activeOnDarkColor} !important;
  }
  &:disabled,
  &.disabled,
  &[aria-disabled="true"] {
    color: ${disabledOnDarkColor};
    border-color: ${disabledOnDarkColor};
  }
`;

export const $IconButton = styled($LinkButton)`
  text-decoration: none;
  // 0.445rem is an awkward amount but it rounds off the height of the icon to keep
  // it nicely squared
  padding: 0.445rem 0.5rem;
  border-radius: 50%;
  svg path {
    fill: ${primaryColor};
  }
  &:focus,
  &:hover,
  &:active {
    background-color: ${({ theme }) => theme.ColorBlurple100} !important;
    // this crisp shadow is called "sticker shadow" in the designs
    // TODO: store drop shadow styles in a unified place so we can keep them consistent
    box-shadow: 0px 1px 0px rgba(33, 40, 102, 0.4);
    svg path {
      fill: ${primaryColor};
    }
  }
  &:disabled,
  &.disabled,
  &[aria-disabled="true"] {
    svg path {
      fill: ${disabledColor};
    }
  }
`;

export const $IconButtonOnDark = styled($IconButton)`
  svg path {
    fill: ${primaryOnDarkColor};
  }
  &:focus,
  &:hover,
  &:active {
    background-color: ${({ theme }) => theme.ColorBlurple400} !important;
    svg path {
      fill: ${primaryOnDarkColor};
    }
  }
  &:focus {
    outline-color: ${activeOnDarkColor} !important;
  }
  &:disabled,
  &.disabled,
  &[aria-disabled="true"] {
    svg path {
      fill: ${disabledOnDarkColor};
    }
  }
`;
